import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, Spinner, Nav, Tooltip, OverlayTrigger, Modal } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { KIEMTRASOLIEU_API, BaseGatewayImage } from '../../../../utils/apiUrl';
import { trangThaiKiemTraSoLieuValue, trangThaiGuiKetQuaValue } from '../../../../utils/define';
import { Get, DeleteSingle } from '../../../../utils/httpProvider';
import { toast } from 'react-toastify';
import moment from 'moment';
import KiemTraSoLieuAdd from './KiemTraSoLieuAdd';
import KiemTraSoLieuEdit from './KiemTraSoLieuEdit';
import DieuKienQuyetDinhForm from './DieuKienQuyetDinhForm';
import { ConfirmModal } from '../../../../modules/modal/ConfirmModal';

const renderTooltip = (props, label) => (
    <Tooltip id="button-tooltip" {...props}>
        {label}
    </Tooltip>
);

const KiemTraSoLieu = ({ hoSoId, activeTab, setActiveTab, isRestricted }) => {
    const queryClient = useQueryClient();
    const [viewMode, setViewMode] = useState('list'); // 'list', 'add', 'edit'
    const [dataEdit, setDataEdit] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showDieuKienModal, setShowDieuKienModal] = useState(false);
    
    // Delete state
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    const { isLoading, data: queryData, refetch } = useQuery(
        ['kiemTraSoLieuData', hoSoId],
        () => Get(`${KIEMTRASOLIEU_API.GET_BY_HOSO_ID}/${hoSoId}`),
        { 
            enabled: !!hoSoId && viewMode === 'list',
            keepPreviousData: true
        }
    );

    // Xử lý data từ API
    let dataList = [];
    if (queryData) {
        if (Array.isArray(queryData)) {
            dataList = queryData;
        } else if (queryData.data && Array.isArray(queryData.data)) {
            dataList = queryData.data;
        } else {
            dataList = [queryData];
        }
    }

    useEffect(() => {
        if (dataList && dataList.length > 0) {
            setSelectedRow(prev => {
                if (prev) {
                    const fresh = dataList.find(item => item.id === prev.id);
                    return fresh || dataList[0];
                }
                return dataList[0];
            });
        } else {
            setSelectedRow(null);
        }
    }, [dataList]);

    const deleteFunc = useMutation((id) => DeleteSingle(KIEMTRASOLIEU_API.DELETE_ITEM, id, null));

    const handleAdd = () => {
        setViewMode('add');
    };

    const handleEdit = (item) => {
        setDataEdit(item);
        setViewMode('edit');
    };

    const handleView = (item) => {
        setDataEdit(item);
        setViewMode('view');
    };

    const deleteAction = (id) => {
        setIdWantDelete(id);
        setConfirmDelMessage('Bản ghi kiểm tra số liệu này');
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;

        deleteFunc.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res && (res.status === 200 || res.status === 201 || res.status === 204 || res.success)) {
                    toast.success('Xóa thành công');
                    queryClient.invalidateQueries(['kiemTraSoLieuData', hoSoId]);
                } else {
                    toast.error(res?.message || 'Có lỗi xảy ra khi xóa');
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Có lỗi xảy ra');
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const renderTrangThai = (val) => {
        switch (val) {
            case trangThaiKiemTraSoLieuValue.choDuyet: return <Badge variant="warning">Chờ duyệt</Badge>;
            case trangThaiKiemTraSoLieuValue.boSung: return <Badge variant="info">Yêu cầu chỉnh sửa</Badge>;
            case trangThaiKiemTraSoLieuValue.hoanThanh: return <Badge variant="success">Hoàn thành</Badge>;
            case trangThaiKiemTraSoLieuValue.tuChoi: return <Badge variant="danger">Từ chối</Badge>;
            default: return <Badge variant="secondary">Chưa xác định</Badge>;
        }
    };

    return (
        <>
        <Card className="h-100 card-sticky-layout">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <Nav variant="pills" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="flex-row">

                        <Nav.Item>
                            <Nav.Link eventKey="quaTrinhLaySoLieu" className="font-weight-bold">Quá trình lấy số liệu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="kiemTraSoLieu" className="font-weight-bold">Kiểm tra số liệu</Nav.Link>
                        </Nav.Item>
                    </Nav>
                    <Button variant="success" size="sm" onClick={handleAdd} disabled={isRestricted}>
                        <i className="fas fa-plus mr-1"></i> Thêm mới
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <>
                        <div className="table-responsive flex-grow-1">
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th className="text-center" width="50">STT</th>
                                        <th className="text-center">Số lượng bài báo</th>
                                        <th>Các tạp chí</th>
                                        <th className="text-center">Ngày dự kiến kiểm tra</th>
                                        <th className="text-center">File tài liệu</th>
                                        <th className="text-center">Trạng thái</th>
                                        <th>Ghi chú</th>
                                    </tr>
                                </thead>
                            <tbody>
                                {dataList.length > 0 ? (
                                    dataList.map((item, index) => (
                                        <tr 
                                            key={item.id}
                                            onClick={() => setSelectedRow(item)}
                                            style={{ cursor: 'pointer' }}
                                            className={selectedRow?.id === item.id ? 'table-primary' : ''}
                                        >
                                            <td className="text-center">{index + 1}</td>
                                            <td className="text-center">{item.soLuongBaiBao || 0}</td>
                                            <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.trenCacTapChi}</td>
                                            <td className="text-center">
                                                {item.ngayKiemTra ? moment(item.ngayKiemTra).format('DD/MM/YYYY') : ''}
                                            </td>
                                            <td className="text-center">
                                                {item.fileTaiLieu ? (
                                                    (() => {
                                                        const path = item.fileTaiLieu;
                                                        const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
                                                        const safePath = path.startsWith('/') ? path : `/${path}`;
                                                        const safeUrl = path.startsWith('http') ? path : `${baseUrl}${safePath}`;
                                                        let name = path.split('/').pop() || 'File';
                                                        name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                                                        return (
                                                            <a href={safeUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-info btn-sm text-left mb-1 mr-1" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                                                                <i className="fas fa-paperclip mr-1"></i> {name}
                                                            </a>
                                                        );
                                                    })()
                                                ) : <span className="text-muted">Chờ HLĐT gửi</span>}
                                            </td>
                                            <td className="text-center">{renderTrangThai(item.trangThai)}</td>
                                            <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.ghiChuChinhSua || ''}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center text-muted">
                                            Chưa có dữ liệu Kiểm tra số liệu
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                        </div>
                        <ConfirmModal
                            isOpen={openConfirmDeleteModal}
                            confirmType="delete"
                            goBackAfterDelete={false}
                            handleAccept={acceptDelete}
                            handleCancel={cancelDelete}
                            content={confirmDelMessage}
                        />
                    </>
                )}
            </Card.Body>
            {!isLoading && (
                <Card.Footer className="bg-white card-footer-sticky">
                    <div className="d-flex justify-content-end pr-4">
                        <Button 
                            variant="info" 
                            size="sm" 
                            onClick={() => handleView(selectedRow)} 
                            className="mr-2"
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-eye mr-1"></i> Xem chi tiết
                        </Button>
                        <Button 
                            variant="warning" 
                            size="sm" 
                            className="mr-2 text-white" 
                            onClick={() => handleEdit(selectedRow)}
                            disabled={!selectedRow || (selectedRow.trangThai !== trangThaiKiemTraSoLieuValue.choDuyet && selectedRow.trangThai !== trangThaiKiemTraSoLieuValue.boSung) || isRestricted}
                        >
                            <i className="fas fa-edit mr-1"></i> Sửa
                        </Button>
                        <Button 
                            variant="danger" 
                            size="sm" 
                            onClick={() => deleteAction(selectedRow?.id)}
                            className="mr-2"
                            disabled={!selectedRow || (selectedRow.trangThai !== trangThaiKiemTraSoLieuValue.choDuyet && selectedRow.trangThai !== trangThaiKiemTraSoLieuValue.boSung) || isRestricted}
                        >
                            <i className="fas fa-trash mr-1"></i> Xóa
                        </Button>
                        <Button 
                            variant="success" 
                            size="sm" 
                            onClick={() => { setDataEdit(selectedRow); setShowDieuKienModal(true); }}
                            disabled={!selectedRow || ![trangThaiGuiKetQuaValue?.daGui, 'DaGui', 1].includes(selectedRow.trangThaiGuiKetQua || selectedRow.TrangThaiGuiKetQua) || isRestricted}
                        >
                            <i className="fas fa-clipboard-check mr-1"></i> Điều kiện quyết định
                        </Button>
                    </div>
                </Card.Footer>
            )}
        </Card>

        {viewMode === 'add' && <KiemTraSoLieuAdd hoSoId={hoSoId} setViewMode={setViewMode} refreshList={refetch} />}
        {viewMode === 'edit' && dataEdit && <KiemTraSoLieuEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refetch} isView={false} isUserView={true} />}
        {viewMode === 'view' && dataEdit && <KiemTraSoLieuEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refetch} isView={true} isUserView={true} />}
        
        {showDieuKienModal && dataEdit && (
            <Modal show={showDieuKienModal} onHide={() => setShowDieuKienModal(false)} backdrop="static" keyboard={false} size="xl">
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>Điều kiện quyết định</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3 custom-modal-body" style={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto', overflowX: 'hidden' }}>
                    <DieuKienQuyetDinhForm 
                        kiemTraSoLieuId={dataEdit.id} 
                        kiemTraSoLieuData={dataEdit} 
                        readOnly={true} 
                        isUserView={true} 
                        refetchParent={refetch} 
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDieuKienModal(false)}>
                        Đóng
                    </Button>
                </Modal.Footer>
            </Modal>
        )}
        </>
    );
};

export default KiemTraSoLieu;
